import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  FindProductSupplierContactValuesQueryResponse,
  FindProductSupplierContactValuesQueryParams,
} from "../types/FindProductSupplierContactValues"

type FindProductSupplierContactValuesClient = typeof client<
  FindProductSupplierContactValuesQueryResponse,
  Error,
  never
>
type FindProductSupplierContactValues = {
  data: FindProductSupplierContactValuesQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: FindProductSupplierContactValuesQueryParams
  headerParams: never
  response: Awaited<ReturnType<FindProductSupplierContactValuesClient>>
  client: {
    parameters: Partial<Parameters<FindProductSupplierContactValuesClient>[0]>
    return: Awaited<ReturnType<FindProductSupplierContactValuesClient>>
  }
}
export const findProductSupplierContactValuesQueryKey = (
  params: FindProductSupplierContactValues["queryParams"]
) =>
  [
    { url: "/v1/product/find-product-supplier-contact-values" },
    ...(params ? [params] : []),
  ] as const
export type FindProductSupplierContactValuesQueryKey = ReturnType<
  typeof findProductSupplierContactValuesQueryKey
>
export function findProductSupplierContactValuesQueryOptions<
  TData = FindProductSupplierContactValues["response"],
  TQueryData = FindProductSupplierContactValues["response"],
>(
  params: FindProductSupplierContactValues["queryParams"],
  options: FindProductSupplierContactValues["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindProductSupplierContactValues["response"],
    FindProductSupplierContactValues["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findProductSupplierContactValuesQueryKey(params)
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindProductSupplierContactValues["data"],
        FindProductSupplierContactValues["error"]
      >({
        method: "get",
        url: `/v1/product/find-product-supplier-contact-values`,
        params,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find product supplier contact values
 * @link /v1/product/find-product-supplier-contact-values
 */
export function useFindProductSupplierContactValues<
  TData = FindProductSupplierContactValues["response"],
  TQueryData = FindProductSupplierContactValues["response"],
  TQueryKey extends QueryKey = FindProductSupplierContactValuesQueryKey,
>(
  params: FindProductSupplierContactValues["queryParams"],
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindProductSupplierContactValues["response"],
        FindProductSupplierContactValues["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindProductSupplierContactValues["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindProductSupplierContactValues["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey =
    queryOptions?.queryKey ?? findProductSupplierContactValuesQueryKey(params)
  const query = useQuery<
    FindProductSupplierContactValues["data"],
    FindProductSupplierContactValues["error"],
    TData,
    any
  >({
    ...findProductSupplierContactValuesQueryOptions<TData, TQueryData>(
      params,
      clientOptions
    ),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindProductSupplierContactValues["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
